import { useMemo } from 'react'
import type { SafetyFunctionWithArchitecture } from '@/shared/hooks/api/machineryDirectiveCompliance'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { ScrollArea } from '@/components/ui/scroll-area'
import { countSistemaArchitecture } from './SistemaBlockDiagram'

type ExportSistemaDialogProps = {
  isOpen: boolean
  onClose: () => void
  onConfirm: () => void
  safetyFunctions: SafetyFunctionWithArchitecture[]
  isExporting: boolean
}

export function ExportSistemaDialog({
  isOpen,
  onClose,
  onConfirm,
  safetyFunctions,
  isExporting,
}: ExportSistemaDialogProps) {
  const { t } = useTranslation('machineryDirectiveCompliance')

  const included = useMemo(() => safetyFunctions, [safetyFunctions])
  const hasSafetyFunctions = included.length > 0

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className='sm:max-w-lg' showCloseButton={!isExporting}>
        <DialogHeader>
          <DialogTitle>
            {t('safetyFunctionsPage.exportDialog.title')}
          </DialogTitle>
        </DialogHeader>
        <p className='text-sm text-muted-foreground'>
          {t('safetyFunctionsPage.exportDialog.description')}
        </p>

        {hasSafetyFunctions ? (
          <div>
            <p className='mb-2 text-sm font-medium'>
              {t('safetyFunctionsPage.exportDialog.includedTitle', {
                count: included.length,
              })}
            </p>
            <ScrollArea className='h-[240px] rounded-md border p-2'>
              <ul className='space-y-3 text-sm'>
                {included.map((safetyFunction) => {
                  const counts = countSistemaArchitecture(safetyFunction)
                  return (
                    <li
                      key={safetyFunction.id}
                      className='flex flex-col gap-0.5'
                    >
                      <span className='font-medium'>{safetyFunction.name}</span>
                      {safetyFunction.sfid ? (
                        <span className='text-xs text-muted-foreground'>
                          {safetyFunction.sfid}
                        </span>
                      ) : null}
                      <span className='text-xs text-muted-foreground'>
                        {t('safetyFunctionsPage.exportDialog.architectureSummary', {
                          components: counts.components,
                          channels: counts.channels,
                          blocks: counts.blocks,
                          elements: counts.elements,
                        })}
                      </span>
                    </li>
                  )
                })}
              </ul>
            </ScrollArea>
          </div>
        ) : (
          <p className='text-sm text-muted-foreground'>
            {t('safetyFunctionsPage.exportDialog.allExcluded')}
          </p>
        )}

        <DialogFooter>
          <Button variant='outline' onClick={onClose} disabled={isExporting}>
            {t('common:actions.cancel')}
          </Button>
          <Button onClick={onConfirm} disabled={isExporting || !hasSafetyFunctions}>
            {isExporting
              ? t('safetyFunctionsPage.exportDialog.exporting')
              : t('safetyFunctionsPage.exportDialog.export')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
